import { DateRange, GeneralLedgerFilters } from 'general-ledger/types';
import React, { createContext, useContext, useReducer } from 'react';
import { JournalEntry, LedgerAccount } from '../types';
import { SidebarDescriptor } from '../../types';
import sidebarTypes from 'general-ledger/sidebarTypes';
import { useLocalStorageState } from './useLocalStorageState';

const defaultInitialState: UseGeneralLedgerContext = {
  dateRange: {
    endDate: undefined,
    startDate: undefined,
  },
  hiddenCategories: [],
  sidebarDescriptor: null,
  search: '',
  selectedJournalEntry: null,
  selectedLedgerAccount: null,
  showAccounts: false,
  showQualityIssues: false,
};

const GeneralLedgerContext = createContext<UseGeneralLedgerContext>(defaultInitialState);

type UseGeneralLedgerProps = {
  initialState: Partial<UseGeneralLedgerState>;
  clientId: string;
};

export type UseGeneralLedgerState = {
  dateRange: DateRange;
  hiddenCategories: string[];
  search: string;
  selectedJournalEntry: JournalEntry | null;
  selectedLedgerAccount: LedgerAccount | null;
  sidebarDescriptor: SidebarDescriptor | null;
  showAccounts: boolean;
  showQualityIssues: boolean;
};

type UseGeneralLedgerContext = UseGeneralLedgerState & {
  updateDateRange?: (dateRange: DateRange) => void;
  updateHiddenCategories?: (hiddenCategories: string[]) => void;
  updateSelectedLedgerAccount?: (ledgerAccount: LedgerAccount) => void;
  updateSelectedJournalEntry?: (journalEntry: JournalEntry) => void;
  clearSidebar?: () => void;
  updateInternalFilters?: (filters: GeneralLedgerFilters) => void;
};

type UseGeneralLedgerReducerAction =
  | { type: 'updateFilters'; data: GeneralLedgerFilters }
  | { type: 'setDateRange'; data: DateRange }
  | { type: 'setHiddenCategories'; data: string[] }
  | {
      type: 'setSelectedLedgerAccount';
      data: LedgerAccount;
    }
  | {
      type: 'setSelectedJournalEntry';
      data: JournalEntry;
    }
  | {
      type: 'clearSidebar';
    };

function reducer(
  state: UseGeneralLedgerState,
  action: UseGeneralLedgerReducerAction,
): UseGeneralLedgerState {
  switch (action.type) {
    case 'updateFilters':
      return { ...state, ...action.data };
    case 'setDateRange':
      return { ...state, dateRange: action.data };
    case 'setHiddenCategories':
      return {
        ...state,
        hiddenCategories: action.data,
      };
    case 'setSelectedLedgerAccount':
      const isSameAsAlreadySelectedLedger = state.selectedLedgerAccount?.id === action.data.id;
      return {
        ...state,
        sidebarDescriptor: isSameAsAlreadySelectedLedger
          ? null
          : {
              sidebarType: sidebarTypes.LEDGER_ACCOUNT as keyof typeof sidebarTypes,
              sidebarElementId: action.data.id,
            },
        selectedLedgerAccount: isSameAsAlreadySelectedLedger ? null : action.data,
      };

    case 'setSelectedJournalEntry':
      const isSameAsAlreadySelectedJournalEntry = state.selectedJournalEntry?.id === action.data.id;
      return {
        ...state,
        sidebarDescriptor: isSameAsAlreadySelectedJournalEntry
          ? null
          : {
              sidebarType: sidebarTypes.JOURNAL_ENTRY as keyof typeof sidebarTypes,
              sidebarElementId: action.data.id,
            },
        selectedJournalEntry: isSameAsAlreadySelectedJournalEntry ? null : action.data,
      };
    case 'clearSidebar':
      return {
        ...state,
        sidebarDescriptor: null,
        selectedLedgerAccount: null,
      };
    default:
      return state;
  }
}

export const GeneralLedgerProvider = ({
  children,
  initialState,
  clientId,
}: React.PropsWithChildren<UseGeneralLedgerProps>): React.ReactElement => {
  const [storedHiddenCategories, setStoredHiddenCategories] = useLocalStorageState<
    UseGeneralLedgerState['hiddenCategories']
  >(`GeneralLedger:HiddenCategories:${clientId}`, []);
  const [state, dispatch] = useReducer(reducer, {
    ...defaultInitialState,
    ...initialState,
    hiddenCategories: [...storedHiddenCategories, ...(initialState.hiddenCategories || [])],
  });

  const updateInternalFilters = (filters: GeneralLedgerFilters) => {
    dispatch({ type: 'updateFilters', data: filters });
  };

  const updateDateRange = (dateRange: DateRange) => {
    dispatch({ type: 'setDateRange', data: dateRange });
  };

  const updateHiddenCategories = (hiddenCategories: string[]) => {
    setStoredHiddenCategories(hiddenCategories);
    dispatch({ type: 'setHiddenCategories', data: hiddenCategories });
  };

  const updateSelectedLedgerAccount = (selectedLedgerAccount: LedgerAccount) => {
    dispatch({ type: 'setSelectedLedgerAccount', data: selectedLedgerAccount });
  };

  const updateSelectedJournalEntry = (selectedJournalEntry: JournalEntry) => {
    dispatch({ type: 'setSelectedJournalEntry', data: selectedJournalEntry });
  };

  const clearSidebar = () => {
    dispatch({ type: 'clearSidebar' });
  };

  return (
    <GeneralLedgerContext.Provider
      value={{
        ...state,
        updateDateRange,
        updateHiddenCategories,
        updateSelectedLedgerAccount,
        updateSelectedJournalEntry,
        clearSidebar,
        updateInternalFilters,
      }}
    >
      {children}
    </GeneralLedgerContext.Provider>
  );
};

export const useGeneralLedger = () => {
  const context = useContext(GeneralLedgerContext);
  return context;
};
